import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AppStoreProvider } from "@/contexts/AppStoreContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { PermissionsProvider } from "@/contexts/PermissionsContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { RealtimeProvider } from "@/providers/RealtimeProvider";
import { DesktopNotificationProvider } from "@/providers/DesktopNotificationProvider";
import { BootstrapProvider } from "@/providers/BootstrapProvider";
import { DesignTokenProvider } from "@/providers/DesignTokenProvider";
import { AppErrorBoundary } from "@/components/error/AppErrorBoundary";

import Index from "./pages/Index";
import Login from "./pages/Login";
import NotFound from "./pages/NotFound";

const PageLoader = () => (
  <div className="flex items-center justify-center h-screen bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchOnMount: false,
      structuralSharing: true,
    },
  },
});

const App = () => {
  return (
    <AppErrorBoundary level="app">
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <DesignTokenProvider>
              <AuthProvider>
                <BootstrapProvider>
                  <PermissionsProvider>
                    <RealtimeProvider>
                      <AppStoreProvider>
                        <DesktopNotificationProvider>
                          <TooltipProvider>
                            <Toaster />
                            <Sonner />
                            <BrowserRouter>
                              <Suspense fallback={<PageLoader />}>
                                <Routes>
                                  <Route path="/" element={<Index />} />
                                  <Route path="/login" element={<Login />} />
                                  <Route path="*" element={<NotFound />} />
                                </Routes>
                              </Suspense>
                            </BrowserRouter>
                          </TooltipProvider>
                        </DesktopNotificationProvider>
                      </AppStoreProvider>
                    </RealtimeProvider>
                  </PermissionsProvider>
                </BootstrapProvider>
              </AuthProvider>
            </DesignTokenProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </AppErrorBoundary>
  );
};

export default App;
